import {
  ASSET_TYPE_TO_WIRE,
  WIRE_TO_ASSET_TYPE,
  isContentProductKind,
  type ContentFile,
  type ContentLink,
  type ContentProductKind,
} from '@/app/audience-content/lib';
import type {
  AudienceDraft,
  AudienceRequestStatus,
  DraftPatch,
  WireContentFile,
  WireContentFilePatch,
  WireContentLink,
  WireDraft,
  WireDraftPatch,
  WireFile,
  WireFilterValue,
} from '../types/draft';
import { normalizeSources } from '@/components/shared/signal-sources';
import { employeeRangeLabel } from '@/lib/utils';
import type { FilterSelections, UploadedFile } from './types';

const WIRE_TO_STATUS: Record<string, AudienceRequestStatus> = {
  draft: 'draft',
  building_audience: 'buildingAudience',
  buildingAudience: 'buildingAudience',
  avails_ready: 'availsReady',
  availsReady: 'availsReady',
  activating: 'activating',
  campaign_active: 'campaignActive',
  campaignActive: 'campaignActive',
  delivered: 'delivered',
  paused: 'paused',
  archived: 'archived',
};

const deserializeStatus = (wire: string): AudienceRequestStatus => WIRE_TO_STATUS[wire] ?? 'draft';

export function serializeFilters(filters: FilterSelections): Record<string, WireFilterValue[]> {
  const out: Record<string, WireFilterValue[]> = {};
  for (const [key, values] of Object.entries(filters)) {
    out[key] = values.map((v) => ({
      id: v.id ?? null,
      name: v.value,
      isCustom: v.type === 'custom',
    }));
  }
  return out;
}

export function serializeFiles(files: UploadedFile[]): WireFile[] {
  // Include files whose upload completed, regardless of whether the user has
  // chosen inclusion/exclusion yet — a file without a type is still part of
  // the draft and must survive resume. BE accepts type=null for drafts.
  return files
    .filter((f) => f.status === 'done' && Boolean(f.id))
    .map((f) => ({
      uploadId: f.id as string,
      type: f.listType,
      listKind: f.listKind,
      name: f.name,
      rowCount: f.rowCount,
    }));
}

export function serializeContentLinks(links: ContentLink[]): WireContentLink[] {
  return links.map((l) => ({
    url: l.url,
    name: l.name,
    contentType: ASSET_TYPE_TO_WIRE[l.type],
  }));
}

export function serializeContentFiles(files: ContentFile[]): WireContentFilePatch[] {
  // ContentFile.id is required and only set after the upload mutation
  // succeeds (see useUploadContentFileMutation → addContentFile). No
  // mid-upload state exists in this slice, so no guard needed.
  return files.map((f) => ({
    uploadId: f.id,
    contentType: ASSET_TYPE_TO_WIRE[f.type],
    name: f.name,
  }));
}

export function deserializeContentLinks(
  wire: WireContentLink[] | undefined,
): ContentLink[] | undefined {
  if (!wire) return undefined;
  // Server doesn't issue ids for jsonb elements. We synthesize index-based
  // ids for in-memory React keys + edit/delete dispatch. These ids are NOT
  // stable across rehydrate cycles — never persist them or compare across
  // saves; consumers must look up by id within the current array only.
  return wire.map((w, idx) => ({
    id: `link-${idx}-${w.contentType}`,
    url: w.url,
    name: w.name,
    type: WIRE_TO_ASSET_TYPE[w.contentType],
  }));
}

export function deserializeContentFiles(
  wire: WireContentFile[] | undefined,
): ContentFile[] | undefined {
  if (!wire) return undefined;
  return wire.map((w) => ({
    id: w.uploadId,
    name: w.name,
    type: WIRE_TO_ASSET_TYPE[w.contentType],
    fileKey: '',
    size: 0,
    status: 'uploaded' as const,
    uploadDate: w.uploadDate ?? null,
  }));
}

export function deserializeRequestedProducts(
  wire: ContentProductKind[] | string[] | undefined,
): ContentProductKind[] {
  if (!wire) return [];
  return (wire as string[]).filter(isContentProductKind);
}

/**
 * Translate a (nested) DraftPatch from the frontend domain model into the
 * (flat) WireDraftPatch the backend expects.
 */
/**
 * NPJB-763 — the play and the domain list, flat on the wire. Kept out of
 * `serializeDraftPatch`, which is already far over Sonar's cognitive-complexity limit.
 */
function serializePlayTargeting(t: NonNullable<DraftPatch['target']>): WireDraftPatch {
  const out: WireDraftPatch = {};
  if (t.play !== undefined) out.play = t.play;
  if (t.companyDomains !== undefined) {
    out.companyDomains = t.companyDomains.domains;
    out.companyDomainsResolvedAt = t.companyDomains.resolvedAt;
    out.sourceIcpAudienceId = t.companyDomains.sourceAudienceId;
  }
  return out;
}

export function serializeDraftPatch(patch: DraftPatch): WireDraftPatch {
  const out: WireDraftPatch = {};
  const t = patch.target;
  if (t) {
    if (t.name !== undefined) out.name = t.name;
    if (t.brief !== undefined) out.brief = t.brief;
    if (t.filters !== undefined) out.filters = serializeFilters(t.filters);
    if (t.files !== undefined) out.files = serializeFiles(t.files);
    if (t.leadGoal !== undefined) out.leadGoal = t.leadGoal;
    if (t.leadsPerCompany !== undefined) out.leadsPerCompany = t.leadsPerCompany;
    if (t.campaignTimeframe !== undefined) out.campaignTimeframe = t.campaignTimeframe;
    // NPJB-552 — two independent keys; each rides only when the store holds it.
    if (t.startDate !== undefined) out.startDate = t.startDate;
    if (t.endDate !== undefined) out.endDate = t.endDate;
    if (t.impressionGoal !== undefined) out.impressionGoal = t.impressionGoal;
    if (t.additionalDetails !== undefined) {
      const trimmed = t.additionalDetails.trim();
      out.additionalDetails = trimmed.length > 0 ? trimmed : null;
    }
    if (t.dealReference !== undefined) {
      const trimmed = t.dealReference?.trim() ?? '';
      out.dealReference = trimmed.length > 0 ? trimmed : null;
    }
    if (t.intentTopicSelections !== undefined) {
      out.intentTopicSelections = t.intentTopicSelections;
    }
    if (t.technographicSelections !== undefined) {
      out.technographicSelections = t.technographicSelections;
    }
    if (t.signalSources !== undefined) {
      out.signalSources = t.signalSources;
    }
    Object.assign(out, serializePlayTargeting(t));
  }
  const c = patch.content;
  if (c) {
    if (c.links !== undefined) out.contentLinks = serializeContentLinks(c.links);
    if (c.files !== undefined) out.contentFiles = serializeContentFiles(c.files);
    if (c.requestedProducts !== undefined) out.contentProducts = c.requestedProducts;
  }
  const p = patch.products;
  if (p) {
    if (p.selectedProducts !== undefined) {
      // NPJB-298 — `slug` is the canonical key on the wire. The store's
      // `SelectedProduct.id` already holds the slug (sourced from
      // `CatalogProduct.id`); we rename the field at the serialize boundary
      // so the BE never sees the legacy UUID-shape from new clients.
      out.productSelections = p.selectedProducts.map((sp) => ({
        slug: sp.id,
        name: sp.name,
        category: sp.category,
      }));
    }
    if (p.budget !== undefined) out.budget = p.budget;
    if (p.productAnswers !== undefined) {
      out.productAnswers = p.productAnswers;
    }
  }
  return out;
}

export function deserializeFilters(
  filters: Record<string, WireFilterValue[]> | undefined,
): FilterSelections | undefined {
  if (!filters) return undefined;
  const out: Record<string, FilterSelections[string]> = {};
  for (const [key, values] of Object.entries(filters)) {
    out[key] = values.map((v) => ({
      id: v.id,
      // NPJB-821 #177 — a picked Employee Range stores its formatted label; rebuild the same.
      label: key === 'employeeRange' && !v.isCustom ? employeeRangeLabel(v.name) : v.name,
      value: v.name,
      type: v.isCustom ? ('custom' as const) : ('standard' as const),
    }));
  }
  return out as FilterSelections;
}

export function deserializeFiles(files: WireFile[] | undefined): UploadedFile[] | undefined {
  if (!files) return undefined;
  return files.map((f) => ({
    uid: f.uploadId,
    id: f.uploadId,
    name: f.name ?? '',
    rowCount: f.rowCount,
    listType: f.type,
    listKind: f.listKind ?? null,
    status: 'done' as const,
  }));
}

/**
 * Translate a (flat) WireDraft from the backend into the (nested) AudienceDraft
 * domain model used everywhere on the frontend.
 */
export function deserializeDraft(wire: WireDraft): AudienceDraft {
  return {
    id: wire.id,
    refId: wire.reqCode,
    status: deserializeStatus(wire.status),
    target: {
      name: wire.name ?? null,
      brief: wire.brief ?? '',
      filters: deserializeFilters(wire.filters) ?? ({} as FilterSelections),
      files: deserializeFiles(wire.files) ?? [],
      leadGoal: wire.leadGoal ?? null,
      leadsPerCompany: wire.leadsPerCompany ?? null,
      campaignTimeframe: wire.campaignTimeframe ?? null,
      startDate: wire.startDate ?? null,
      endDate: wire.endDate ?? null,
      impressionGoal: wire.impressionGoal ?? null,
      additionalDetails: wire.additionalDetails ?? '',
      dealReference: wire.dealReference ?? null,
      intentTopicSelections: wire.intentTopicSelections ?? [],
      // ?? [] matters for drafts saved BEFORE this field existed: the key is simply absent, and
      // the store's actions assume an array.
      technographicSelections: wire.technographicSelections ?? [],
      // A retired source must not keep narrowing the audience, so unknown keys drop here.
      signalSources: normalizeSources(wire.signalSources ?? []),
      play: wire.play ?? null,
      companyDomains: {
        domains: wire.companyDomains ?? [],
        resolvedAt: wire.companyDomainsResolvedAt ?? null,
        sourceAudienceId: wire.sourceIcpAudienceId ?? null,
      },
    },
    content: {
      links: deserializeContentLinks(wire.contentLinks) ?? [],
      files: deserializeContentFiles(wire.contentFiles) ?? [],
      // Allow-list filter — drops kinds the FE doesn't know about (e.g. legacy
      // rows from a deprecated kind, or BE-side schema expansion ahead of FE
      // deploy). Unknown values would otherwise reach the chip/label lookups
      // and render as undefined CSS / empty text.
      requestedProducts: deserializeRequestedProducts(wire.contentProducts),
    },
    products: {
      // NPJB-298 — the wire carries `slug` going forward; legacy rows that
      // only have `id` (UUID) get mapped through the BE's response-side
      // normalizer before reaching us, so `slug` is always present.
      selectedProducts: (wire.productSelections ?? []).map((s) => ({
        id: s.slug,
        name: s.name ?? '',
        category: s.category ?? '',
      })),
      budget: wire.budget ?? null,
      productAnswers: wire.productAnswers ?? {},
    },
    campaignId: wire.campaignId,
    updatedAt: wire.updatedAt,
    createdAt: wire.createdAt,
    following: wire.following ?? false,
  };
}
